import { useState, Fragment } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  DefaultSubtitle,
  ReviewCardContainer,
} from '../../../components/application/defaultComponents'
import ApplicationPrebuiltFooter from '../../../components/application/PrebuiltBodyFooter'
import { useApplication } from 'src/context/application'
import { useNavigate, createSearchParams, useLocation } from 'react-router-dom'
import Button from 'src/components/basic/Button'
import { IoMdCheckmark } from 'react-icons/io'
import { ReviewData } from 'src/dtos/applicationData'
import StandardInput from 'src/components/basic/StandardInput'
import FilePicker from 'src/components/basic/FilePicker'
import { uploadFiles } from 'src/services/apiClient'
import { handleYesNoAnswers, lookupYearsInPrivatePractice } from 'src/utils/formDataManipulation'
import { onlyICOCAttorneys } from 'src/utils/newApplications'
import {FILE_SIZE_LABEL} from "../../../utils/constants";

const Attorneys = ({screenshotMode = false}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { application, saveApplication } = useApplication()
  const [review, setReview] = useState<ReviewData>(application?.attorneysSection?.review ?? {})
  const [showNotesInput, setShowNotesInput] = useState(!!application?.attorneysSection?.review?.note)
  const [showFilePicker, setShowFilePicker] = useState(!!application?.attorneysSection?.review?.files)

  const handleEditClick = () => {
    navigate({
      pathname: '/attorneys/main',
      search: createSearchParams({ review: 'attorneys' }).toString()
    })
  }

  const updatedApplication = () => ({
    ...application,
    currentPage: window.location.pathname,
    stoppedAt: pathname,
    attorneysSection: {
      ...application?.attorneysSection,
      review
    }
  })

  const handleSaveApplication = async () => {
    try {
      await saveApplication(updatedApplication())
    } catch(_) {}
  }

  const produceApplicationData = (key: keyof ReviewData, value: any) => ({
    ...application,
    stoppedAt: pathname,
    attorneysSection: {
      ...application?.attorneysSection,
      review: {
        ...application?.attorneysSection?.review,
        [key]: value
      }
    }
  })

  const handleRemoveNote = async () => {
    const removedNoteApplication = produceApplicationData('note', undefined)
    setReview(prev => ({ ...prev, note: undefined }))
    setShowNotesInput(false)
    await saveApplication(removedNoteApplication)
  }

  const handleUploadFiles = async (files: File[]) => {
    if(files.length > 0) {
      const uploadedFiles = await uploadFiles(files)

      const updatedFiles = [...(review?.files ?? []), ...(uploadedFiles ?? [])]
  
      const applicationFileAttached = produceApplicationData('files', updatedFiles)
      
      setReview(prev => ({ ...prev, files: updatedFiles }))
      await saveApplication(applicationFileAttached)
    } else {
      setReview(prev => ({ ...prev, files: undefined }))
      const applicationFileAttached = produceApplicationData('files', undefined)
      await saveApplication(applicationFileAttached)
    }
  }

  const attorneysSection = application?.attorneysSection
  const icocAttorneys = onlyICOCAttorneys(application)

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Almost done! Please review your attorney information before you sign.</DefaultTitle>
        <DefaultSubtitle>If any answers are innacurate or incomplete you can use the edit button to change them.</DefaultSubtitle>
        <ReviewCardContainer id='attorneysData'>
          <header>
            <div>
              <h4>Attorney</h4>
              {!screenshotMode && (
                <h6>
                  <IoMdCheckmark />
                  Complete
                </h6>
              )}
            </div>
            {!screenshotMode && (
              <div>
                <Button style={{padding: '8px 46px'}} onClick={handleEditClick}>Edit Section</Button>
              </div>
            )}
          </header>
          <main className='attorneys'>
            <h5>ATTORNEYS</h5>
            {application?.attorneysSection?.attorneys?.map((attorney, index) => (
              <article key={index}>
                <strong>{[attorney?.firstName, attorney?.lastName].join(' ')}</strong>
                <span><h6>Position:</h6> {attorney?.position}</span>
                <span><h6>Email:</h6> {attorney?.emailAddress}</span>
                <span>
                  <h6>Years in Private Practice:</h6>
                  {
                    lookupYearsInPrivatePractice(attorney?.yearsInPrivatePractice)
                  }
                </span>
                <span><h6>License in Texas:</h6> {!!attorney?.license?.isLicensedInTexas ? 'Yes' : 'No'}</span>
                {!!attorney?.license?.isLicensedInTexas && (
                  <>
                    <span><h6>Texas Bar Card Number:</h6> {attorney?.barCardNumber}</span>
                    <span><h6>Date Licensed in Texas:</h6> {attorney?.license?.licenseDate}</span>
                  </>
                )}
                <span><h6>License in Another State:</h6> {attorney?.license?.isLicensedInOtherState ? 'Yes' : 'No'}</span>
                {!!attorney?.license?.isLicensedInOtherState && (
                  <>
                    <span><h6>Other State Bar Card Number:</h6> {attorney?.outSideTexasBarCardNumber}</span>
                    <span><h6>Another State of License:</h6> {attorney?.license?.otherState}</span>
                    <span><h6>License Date in Another State:</h6> {attorney?.license?.dateLicensedInOtherState}</span>
                    <span><h6>Percentage of time "<u>CURRENTLY</u>" practicing in this jurisdiction :</h6> {attorney?.license?.percentageOfWorkSpent}</span>
                    <span><h6>License(s) in Another State Info:</h6> {attorney?.license?.licensesInfo}</span>
                  </>
                )}
                {!!attorney?.workHistory?.joinedDate && (
                  <span><h6>Date joined present firm:</h6> {attorney?.workHistory?.joinedDate}</span>
                )}
                <span><h6>Average hours per week anticipated during the next year for this firm:</h6> {attorney?.workHistory?.hoursPerWeek}</span>
              </article>
            ))}

            <h5>RELATIONSHIPS</h5>
            <article>
              <strong>How many attorneys have left the firm in the past 12 months?</strong>
              <span>{attorneysSection?.attorneysLeftFirmQuantity ?? 'N/A'}</span>
            </article>
            <article>
              <strong>Does any lawyer(s) act as an independent contractor (IC) and/or "Of Counsel" (OC) to your firm or is listed as such on your firm's letterhead or website?</strong>
              <span>{handleYesNoAnswers(attorneysSection?.hasICOCAttorneys)}</span>
            </article>
            <article>
              <strong>Does any firm member act as “Of Counsel” or Independent Contract Attorney to another law firm?</strong>
              <span>{handleYesNoAnswers(attorneysSection?.hasICOCInAnotherFirm)}</span>
              {attorneysSection?.hasICOCInAnotherFirm === true ? (
                <span>{attorneysSection?.hasICOCInAnotherFirmDescription ?? 'N/A'}</span>
              ) : null}
            </article>
            <article>
              <strong>Is Applicant a sole practitioner?</strong>
              <span>{handleYesNoAnswers(attorneysSection?.isSolePractitioner)}</span>
              {attorneysSection?.isSolePractitioner === true ? (
                <>
                  <br />
                  <strong>How many hours does Applicant work on average per week (not including work performed as an Independent Contractor or Of Counsel)?</strong>
                  <span><h6>This year: </h6>{attorneysSection?.solePractitionerThisYear ?? 'N/A'}</span>
                  <span><h6>Last year: </h6>{attorneysSection?.solePractitionerLastYear ?? 'N/A'}</span>
                  <span><h6>Two years ago: </h6>{attorneysSection?.solePractitionerTwoYearsAgo ?? 'N/A'}</span>
                  <span><h6>Three years ago: </h6>{attorneysSection?.solePractitionerFourYearsAgo ?? 'N/A'}</span>
                  <span><h6>If less than 40 hours, please provide the reason: </h6>{attorneysSection?.reasonLessThanFourtyHours ?? 'N/A'}</span>
                </>
              ) : null}
            </article>

            {attorneysSection?.hasICOCAttorneys === true ? (
              <>
                <h5>INDEPENDENT CONTRACTOR/OF COUNSEL</h5>
                {icocAttorneys?.map(attorney => (
                  <Fragment key={attorney.barCardNumber}>
                    <article>
                      <strong>{[attorney?.firstName, attorney?.lastName].join(' ')}</strong>
                      <span><h6>Year attorney became Independent Contractor or Of Counsel</h6></span>
                      <span>{attorney?.yearStartedAsICOC ?? 'N/A'}</span>
                      <span><h6>In the past 12 months, how many hours did the Attorney bill on behalf of your firm?</h6></span>
                      <span>{attorney?.hoursOnBill ?? 'N/A'}</span>
                      <span><h6>
                      If the attorney has been acting as an Independent Contractor or Of Counsel for your firm for less than 12 months, how many hours do you 
                      estimate the attorney will bill on behalf of your firm during their first year?
                      </h6></span>
                      <span>{attorney?.hoursBilledInFirstYear ?? 'N/A'}</span>
                      <span><h6>Does the attorney also practice independently from your firm?</h6></span>
                      <span>{handleYesNoAnswers(attorney?.actsIndependentlyFromFirm)}</span>
                      {attorney?.actsIndependentlyFromFirm === true ? (
                        <>
                          <span><h6>Where does the attorney practice independently from your firm? </h6></span>
                          <span>{attorney?.actsIndependentlyFromFirmWhere ?? 'N/A'}</span>
                        </>
                      ) : null}
                      <span><h6>Does the Attorney have professional liability insurance for that independent practice?</h6></span>
                      <span>{handleYesNoAnswers(attorney?.hasInsuranceForIndependentPractice)}</span>
                      {attorney?.hasInsuranceForIndependentPractice === true ? (
                        <>
                          <span><h6>Name of the insurance company? </h6>{attorney?.insuranceCompanyNameOfIndependent ?? 'N/A'}</span>
                          <span><h6>Effective dates of the policy:</h6></span>
                          <span><h6>From: </h6>{attorney?.effectivePolicyDateFrom ?? 'N/A'}</span>
                          <span><h6>To: </h6>{attorney?.effectivePolicyDateTo ?? 'N/A'}</span>
                          <span><h6>Limits of liability of the policy: </h6>{attorney?.liabilityLimitsOfPolicy ? attorney?.liabilityLimitsOfPolicy : 'N/A'}</span>
                        </>
                      ) : null}
                      <span><h6>Which of the following tasks does the attorney perform on behalf of your firm?</h6></span>
                      {(attorney?.tasksPerformed && attorney?.tasksPerformed.length > 0) ? 
                      attorney.tasksPerformed.map(task => (
                        <span key={task}>{task ?? 'N/A'}</span>
                      )) : null}
                    </article>
                  </Fragment>
                ))}
              </>
            ) : null}
          </main>
          {!screenshotMode && (
            <footer>
              {showNotesInput ? (
                <div className="review-footer-note-container">
                  <StandardInput
                    label=""
                    rows={15}
                    value={review?.note ?? ''}
                    onChange={e => setReview(prev => ({ ...prev, note: e.target.value }))}
                  />
                  <div className="review-footer-note-buttons">
                    {!!application?.attorneysSection?.review?.note ? (
                      <Button
                        variant="error"
                        onClick={handleRemoveNote}
                      >
                        Remove note
                      </Button>
                    ) : (
                      <>
                        <Button
                          className="review-button-small-width"
                          onClick={handleSaveApplication}
                        >
                          Save details
                        </Button>
                        <Button
                          variant="error"
                          className="review-button-small-width"
                          onClick={() => {
                            setReview(prev => ({ ...prev, note: undefined }))
                            setShowNotesInput(false)
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowNotesInput(true)}
                  >
                    Add a note
                  </Button>
                </div>
              )}
              {showFilePicker ? (
                <div className='review-footer-filepicker-container'>
                  <FilePicker
                    title={FILE_SIZE_LABEL} 
                    onFilesSelect={handleUploadFiles}
                    selectedFiles={review?.files ?? []}
                    accept='.doc,.docx,application/msword, application/pdf'
                    limit={10}
                  />
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowFilePicker(true)}
                  >
                    Attach a document
                  </Button>
                </div>
              )}
            </footer>
          )}
        </ReviewCardContainer>
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute="/review/firm"
        nextRoute="/review/insurance"
        updatedApplication={updatedApplication()}
      />
    </ApplicationBodyContainer>
  )
}

export default Attorneys